import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    MenuItem,
} from "@mui/material";
import {
    useCreateSubCategoryMutation,
    useUpdateSubCategoryMutation,
    useGetCategoriesQuery,
} from "../../../redux/service/modules/admin";
import { X } from "lucide-react";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import { useForm, Controller } from "react-hook-form";
import { useNotification } from "../../../context/NotificationContext";
import CommonDialog from "../../../components/common/CommonDialog";

const SubCategoryDialog = ({
    open,
    onClose,
    rowData,
    isEdit,
    title,
    ctaText,
}) => {
    const { control, handleSubmit, setValue, reset, setError } = useForm();
    const [createSubCategory, { isLoading: isCreating }] =
        useCreateSubCategoryMutation();
    const [updateSubCategory, { isLoading: isUpdating }] =
        useUpdateSubCategoryMutation();
    const { showNotification } = useNotification();
    const { data: categories } = useGetCategoriesQuery({});

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                if (key === "category_id") {
                    setValue("category", rowData[key]);
                } else {
                    setValue(key, rowData[key]);
                }
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(data).forEach((key) => {
                formDataToSend.append(key, data[key]);
            });
            if (isEdit) {
                await updateSubCategory({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Sub Category Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Sub Category"
                            );
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("sub_category_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            } else {
                await createSubCategory({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Sub Category Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Sub Category"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("sub_category_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update sub category:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <Controller
                name="category"
                control={control}
                rules={{
                    required: "Category is required",
                }}
                render={({ field, fieldState }) => (
                    <FormField
                        label="Category"
                        select
                        {...field}
                        required
                        error={!!fieldState.error}
                        helperText={
                            fieldState.error ? fieldState.error.message : ""
                        }
                    >
                        <option value="" disabled selected>
                            Select Category
                        </option>
                        {categories?.data?.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.category_name}
                            </option>
                        ))}
                    </FormField>
                )}
            />
            <Controller
                name="sub_category_name"
                control={control}
                rules={{
                    required: "Sub category name is required",
                    pattern: {
                        value: /^[a-zA-Z0-9\s\-\&\(\)\;\\/]+$/,
                        message:
                            "Sub category name can only contain letters, numbers, spaces, hyphens, ampersands, semicolons, forward slashes and parentheses",
                    },
                }}
                render={({ field, fieldState }) => (
                    <FormField
                        label="Sub Category Name"
                        {...field}
                        required
                        error={!!fieldState.error} // Show error if exists
                        helperText={
                            fieldState.error ? fieldState.error.message : ""
                        } // Display error message
                    />
                )}
            />

            <ActionButtons
                onCancel={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isCreating || isUpdating}
            />
        </CommonDialog>
    );
};

SubCategoryDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
};

export default SubCategoryDialog;
